<template>
  <main-template>
    <v-container>
      <v-row align="center" class="mb-1">
        <v-col>
          <h1>
            <v-btn color="primary" x-small text @click="$router.go(-1)">
              <v-icon left>fa fa-angle-left</v-icon>
            </v-btn>
            Aparelhos de Reconhecimento Facial
          </h1>
        </v-col>
      </v-row>

      <v-alert type="info" text dense class="mb-4">
        <div class="font-weight-bold mb-1">Como ler o status</div>
        <div>
          <strong>Ativo</strong>: recebemos ao menos uma presença deste aparelho nas últimas
          {{ horasParaInativo }} horas.
        </div>
        <div>
          <strong>Inativo</strong>: nenhuma presença chegou nas últimas
          {{ horasParaInativo }} horas. Isso não quer dizer que o aparelho está com defeito: pode
          ser um dia sem expediente, fim de semana ou feriado. Vale verificar só se ele continuar
          inativo em dias de trabalho.
        </div>
        <div class="mt-1">
          Os aparelhos entram na lista sozinhos, ao enviarem o primeiro registro de ponto.
          <span v-if="podeVincular">
            Os que aparecem <strong>sem franquia</strong> precisam ser vinculados.
          </span>
        </div>
      </v-alert>

      <v-card outlined>
        <v-card-text>
          <v-row dense align="center">
            <v-col cols="12" md="5">
              <v-text-field
                v-model="busca"
                label="Buscar por nome, MAC, IP ou descrição"
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filtroStatus"
                :items="opcoesStatus"
                label="Status"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col v-if="podeVincular" cols="12" sm="6" md="4">
              <v-autocomplete
                v-model="filtroFranquia"
                :items="opcoesFiltroFranquia"
                item-text="nome"
                item-value="id"
                label="Franquia"
                outlined
                dense
                clearable
                hide-details
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-data-table
          :headers="headers"
          :items="aparelhosFiltrados"
          :loading="carregando"
          :items-per-page="25"
          loading-text="Carregando aparelhos..."
          no-data-text="Nenhum aparelho enviou registros ainda."
          no-results-text="Nenhum aparelho encontrado com esses filtros."
        >
          <template v-slot:item.nome="{ item }">
            <div class="font-weight-bold">{{ item.nome || "Sem nome" }}</div>
            <div class="text-caption grey--text">
              <span v-if="item.mac_address">MAC {{ item.mac_address }}</span>
              <span v-if="item.mac_address && item.ip"> · </span>
              <span v-if="item.ip">IP {{ item.ip }}</span>
            </div>
          </template>

          <template v-slot:item.franquia="{ item }">
            <span v-if="item.franquia">{{ item.franquia }}</span>
            <v-chip v-else small color="orange lighten-4" text-color="orange darken-4">
              Sem franquia
            </v-chip>
          </template>

          <template v-slot:item.descricao="{ item }">
            <span v-if="item.descricao">{{ item.descricao }}</span>
            <span v-else class="grey--text">—</span>
          </template>

          <template v-slot:item.ativo="{ item }">
            <v-chip
              small
              :color="item.ativo ? 'green lighten-4' : 'grey lighten-3'"
              :text-color="item.ativo ? 'green darken-3' : 'grey darken-2'"
              class="font-weight-bold"
            >
              <v-icon left x-small>{{
                item.ativo ? "mdi-check-circle" : "mdi-clock-outline"
              }}</v-icon>
              {{ item.ativo ? "Ativo" : "Inativo" }}
            </v-chip>
          </template>

          <template v-slot:item.ultima_presenca_em="{ item }">
            <span v-if="item.ultima_presenca_em">{{
              formatarDataHora(item.ultima_presenca_em)
            }}</span>
            <span v-else class="grey--text">Nenhuma presença recebida</span>
          </template>

          <template v-slot:item.acoes="{ item }">
            <v-btn small text color="primary" @click="abrirEdicao(item)">
              <v-icon left small>mdi-pencil-outline</v-icon>
              Editar
            </v-btn>
          </template>
        </v-data-table>
      </v-card>

      <v-dialog v-model="dialog" max-width="520" persistent>
        <v-card v-if="editando">
          <v-card-title class="text-subtitle-1 font-weight-bold">
            <v-icon left>mdi-face-recognition</v-icon>
            {{ editando.nome || "Aparelho" }}
          </v-card-title>
          <v-card-subtitle v-if="editando.mac_address" class="pt-1">
            MAC {{ editando.mac_address }}
          </v-card-subtitle>
          <v-card-text>
            <v-autocomplete
              v-if="podeVincular"
              v-model="form.configuracao_id"
              :items="franquias"
              item-text="nome"
              item-value="id"
              label="Franquia"
              hint="Franquia onde o aparelho está instalado"
              persistent-hint
              outlined
              dense
              clearable
              class="mb-4"
            />
            <v-textarea
              v-model="form.descricao"
              label="Descrição"
              placeholder="Ex.: Recepção do térreo"
              outlined
              dense
              rows="3"
              counter="500"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text :disabled="salvando" @click="dialog = false">Cancelar</v-btn>
            <v-btn
              color="primary"
              :loading="salvando"
              :disabled="salvando || (form.descricao || '').length > 500"
              @click="salvar"
            >
              Salvar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </main-template>
</template>

<script>
const SEM_FRANQUIA = "sem-franquia";

export default {
  name: "AparelhosFaciaisPage",
  data() {
    return {
      carregando: false,
      salvando: false,
      aparelhos: [],
      franquias: [],
      podeVincular: false,
      horasParaInativo: 24,
      busca: "",
      filtroStatus: "todos",
      filtroFranquia: null,
      opcoesStatus: [
        { text: "Todos", value: "todos" },
        { text: "Ativos", value: "ativos" },
        { text: "Inativos", value: "inativos" },
      ],
      dialog: false,
      editando: null,
      form: { configuracao_id: null, descricao: "" },
    };
  },
  computed: {
    headers() {
      const headers = [
        { text: "Aparelho", value: "nome" },
        { text: "Franquia", value: "franquia" },
        { text: "Descrição", value: "descricao", sortable: false },
        { text: "Status", value: "ativo" },
        { text: "Última presença", value: "ultima_presenca_em" },
        { text: "", value: "acoes", sortable: false, align: "end" },
      ];
      return this.podeVincular ? headers : headers.filter((h) => h.value !== "franquia");
    },
    opcoesFiltroFranquia() {
      return [{ id: SEM_FRANQUIA, nome: "Sem franquia" }, ...this.franquias];
    },
    aparelhosFiltrados() {
      const termo = (this.busca || "").trim().toLowerCase();
      return this.aparelhos.filter((a) => {
        if (this.filtroStatus === "ativos" && !a.ativo) return false;
        if (this.filtroStatus === "inativos" && a.ativo) return false;
        if (this.filtroFranquia === SEM_FRANQUIA && a.configuracao_id) return false;
        if (
          this.filtroFranquia &&
          this.filtroFranquia !== SEM_FRANQUIA &&
          a.configuracao_id !== this.filtroFranquia
        ) {
          return false;
        }
        if (!termo) return true;
        return [a.nome, a.mac_address, a.ip, a.descricao, a.franquia]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(termo));
      });
    },
  },
  mounted() {
    this.carregar();
  },
  methods: {
    async carregar() {
      this.carregando = true;
      try {
        const resposta = await this.$services.aparelhosFaciaisService.listar();
        this.aparelhos = resposta.aparelhos || [];
        this.franquias = (resposta.franquias || []).map((f) => ({
          id: f.id,
          nome: f.descricao || f.fantasia,
        }));
        this.podeVincular = Boolean(resposta.pode_vincular);
        this.horasParaInativo = resposta.horas_para_inativo || 24;
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregando = false;
      }
    },

    abrirEdicao(aparelho) {
      this.editando = aparelho;
      this.form = {
        configuracao_id: aparelho.configuracao_id,
        descricao: aparelho.descricao || "",
      };
      this.dialog = true;
    },

    async salvar() {
      const dados = { descricao: this.form.descricao || null };
      if (this.podeVincular) {
        dados.configuracao_id = this.form.configuracao_id || null;
      }

      this.salvando = true;
      try {
        const { aparelho } = await this.$services.aparelhosFaciaisService.atualizar(
          this.editando.id,
          dados
        );
        this.aparelhos = this.aparelhos.map((a) => (a.id === aparelho.id ? aparelho : a));
        this.$toast.success("Aparelho atualizado.");
        this.dialog = false;
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.salvando = false;
      }
    },

    formatarDataHora(iso) {
      return new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>
